import {
    DefaultButton, IconButton, Label, TextField,
} from '@fluentui/react'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import useTranslate from 'helpers/hooks/useTranslate'
import React, { useCallback } from 'react'
import { Columns } from 'react-bulma-components'
import Merchandise from 'requests/objects/merchandise'
import Param from 'requests/objects/param'
import styles from 'styles/components/pages/order-quotes/[id]/content.module.scss'
import classNames from 'classnames'
import parseJson from 'helpers/methods/parseJson'
import { ErrorOrderQuote } from 'requests/objects/order-quote'
import TextFieldWithSideButton from 'components/inputs/textFieldWithSideButton'

/**
 * MerchandiseStepContent
 * @param {object} props Props
 * @param {object} props.lang Lang
 * @param {Merchandise[]} props.items Items
 * @param {(merchandises: Merchandise[]) => void} props.onUpdate onUpdate
 * @param {Param} props.param Param
 * @param {boolean} props.isReadOnly Param
 * @param {ErrorOrderQuote} props.errorField errorField
 * @param {boolean} props.isFootageInformable isFootageInformable
 * @returns {JSX.Element} Element
 */
export default function MerchandiseStepContent({
    lang, items = [], onUpdate, isReadOnly, param, errorField, isFootageInformable = true,
}) {
    const { tKey, tObj } = useTranslate({ lang })

    const removeMerchandise = useCallback(
        /**
         * @param {number} merchandiseIndex Index
         */
        merchandiseIndex => {
            onUpdate(items.filter((_, i) => i !== merchandiseIndex))
        }, [items, onUpdate],
    )

    const calcFootage = (long, width) => {
        if (long === null || long === undefined || width === null || width === undefined)
            return null

        const l = Number(long)
        const w = Number(width)

        if (!Number.isFinite(l) || !Number.isFinite(w))
            return null

        const value = ((l / 100) * (w / 100)) / 2.4
        const rounded = (Math.round(value * 100) / 100) === 0 ? 0.01 : Math.round(value * 100) / 100

        return rounded
    }

    const hasPositiveFootageDimensions = (long, width) => Number(long) > 0 && Number(width) > 0

    const getArticleFootage = article => (article?.footage ? Number(article.footage) : null)

    const hasPositiveArticleFootage = article => Number(article?.footage) > 0

    const getMissingFootageMessage = ({ article, merchandise }) => {
        if (!article || merchandise.isManualArticle || isFootageInformable)
            return ''

        if (hasPositiveArticleFootage(article))
            return ''

        if (article.calcFootage && !hasPositiveFootageDimensions(merchandise.long, merchandise.width))
            return tKey('footageCannotBeDetermined')

        if (!article.calcFootage)
            return tKey('articleDefaultFootageMissing')

        return ''
    }

    const updateMerchandise = useCallback(
        (merchandiseIndex, data) => {
            const newItems = items.map((merchandise, i) => {
                if (i !== merchandiseIndex)
                    return merchandise

                // 1) Si articleId change
                if (data.articleId && data.articleId !== merchandise.articleId) {
                    const article = param.articles.find(a => a.key === data.articleId)

                    if (!article)
                        return { ...merchandise, ...data }

                    return {
                        ...merchandise,
                        ...data,
                        long: article.long ? +article.long : null,
                        width: article.width ? +article.width : null,
                        height: article.height ? +article.height : null,
                        weight: article.weight ? +article.weight : null,
                        // eslint-disable-next-line no-nested-ternary
                        footage: article.calcFootage && hasPositiveFootageDimensions(article.long, article.width)
                            ? calcFootage(article.long, article.width)
                            : getArticleFootage(article),
                        calcFootage: article.calcFootage,
                    }
                }

                // 2) Si long/width change et l’autre dimension existe déjà
                if (
                    (data.long > 0 && merchandise.width > 0)
                    || (data.width > 0 && merchandise.long > 0)
                ) {
                    const article = param.articles.find(x => x.key === merchandise.articleId)
                    const shouldCalcFootage = merchandise.isManualArticle || article?.calcFootage

                    if (shouldCalcFootage) {
                        const long = data.long ?? merchandise.long
                        const width = data.width ?? merchandise.width
                        const footage = calcFootage(long, width)

                        return { ...merchandise, ...data, footage }
                    }

                    if (article?.calcFootage) {
                        const long = data.long ?? merchandise.long
                        const width = data.width ?? merchandise.width
                        const footage = calcFootage(long, width)

                        return { ...merchandise, ...data, footage }
                    }
                    // 3) Si long ou width devient 0 => retour au footage par défaut de l’article
                } else if (data.long === 0 || data.width === 0) {
                    const article = param.articles.find(x => x.key === merchandise.articleId)

                    // Retour au footage par défaut de l’article.
                    if (article)
                        return {
                            ...merchandise,
                            ...data,
                            footage: getArticleFootage(article),

                        }
                }

                return { ...merchandise, ...data }
            })

            onUpdate(newItems)
        },
        [items, onUpdate, param.articles],
    )

    return (
        <div className={styles['delivery-step-content']}>
            {items.map((merchandise, merchandiseIndex) => {
                const canEditFootage = merchandise.isManualArticle === true || isFootageInformable === true
                // eslint-disable-next-line max-len
                const emptyMerchandiseError = errorField[`deliveryContentData.merchandises.${merchandiseIndex}.merchandises`]
                    ? tKey('emptyMerchandiseError') : ''

                const articleIdError = tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.articleId`])
                    ? tKey('anArticleMustBeSelected') : ''
                const article = merchandise.articleId && param.articles.find(x => x.key === merchandise.articleId)
                const footageError = tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.footage`])
                const missingFootageMessage = getMissingFootageMessage({ article, merchandise })

                return (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${merchandise.merchandiseId}_${merchandiseIndex}`}
                        className={styles['delivery-step-content-main']}
                    >
                        <Label required>
                            {tKey('handlingUnit')}
                            {' '}
                            {merchandiseIndex + 1}
                        </Label>
                        <Columns
                            className={classNames(
                                'is-gapless',
                                styles['delivery-step-content-main-support'],
                            )}
                        >
                            <Columns.Column>
                                {merchandise.isManualArticle ? (
                                    <TextFieldWithSideButton
                                        placeholder={tKey('articleName')}
                                        value={merchandise.articleName || ''}
                                        readOnly={isReadOnly}
                                        onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                            articleName: newVal,
                                        })}
                                        errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.articleName`])}
                                        buttonIconName="Cancel"
                                        buttonMessage={tKey('cancelManualArticle')}
                                        buttonOnClick={() => updateMerchandise(merchandiseIndex, {
                                            isManualArticle: false,
                                            articleName: '',
                                        })}
                                    />
                                ) : (
                                    <FilteredVirtualCombobox
                                        placeholder={tKey('select')}
                                        options={param.articles.map(s => ({
                                            key: s.key,
                                            text: tObj(parseJson(s.text)),
                                            data: s.subText,
                                        }))}
                                        selectedKey={merchandise.articleId}
                                        readOnly={isReadOnly}
                                        onChange={(ev, option) => updateMerchandise(merchandiseIndex, {
                                            articleId: option.key,
                                        })}
                                        errorMessage={[emptyMerchandiseError, articleIdError].filter(Boolean).join(', ')}
                                        addButtonProps={{
                                            addName: tKey('addManualArticle'),
                                            onAddClick: () => updateMerchandise(merchandiseIndex, {
                                                isManualArticle: true,
                                                articleId: 0,
                                            }),
                                        }}
                                    />
                                )}
                            </Columns.Column>
                            {/* <RequiredDisplay isReadOnly={isReadOnly} /> */}
                            <Columns.Column>
                                <TextField
                                    placeholder={tKey('reference')}
                                    value={merchandise.reference?.toString() || ''}
                                    readOnly={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        reference: newVal,
                                    })}
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.reference`])}
                                // required={!isReadOnly}
                                />
                            </Columns.Column>
                            <Columns.Column>
                                <TextField
                                    placeholder={tKey('quantity')}
                                    value={merchandise.quantity?.toString() || ''}
                                    readOnly={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        quantity: +newVal,
                                    })}
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.quantity`])}
                                    required={!isReadOnly}
                                    type="number"
                                    suffix="Q."
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns
                            className={classNames(
                                'is-gapless',
                                styles['delivery-step-content-main-data'],
                            )}
                        >
                            <Columns.Column className={styles['delivery-step-content-main-size']}>
                                <TextField
                                    placeholder={tKey('long')}
                                    value={merchandise.long?.toString() || ''}
                                    readOnly={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        long: +newVal > 700 ? 700 : +newVal,
                                    })}
                                    // eslint-disable-next-line max-len
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.long`])}
                                    required={!isReadOnly}
                                    type="number"
                                />
                                <TextField
                                    placeholder={tKey('width')}
                                    value={merchandise.width?.toString() || ''}
                                    readOnly={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        width: +newVal > 245 ? 245 : +newVal,
                                    })}
                                    // eslint-disable-next-line max-len
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.width`])}
                                    required={!isReadOnly}
                                    type="number"
                                />
                                <TextField
                                    placeholder={tKey('height')}
                                    value={merchandise.height?.toString() || ''}
                                    readOnly={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        height: +newVal > 700 ? 700 : +newVal,
                                    })}
                                    // eslint-disable-next-line max-len
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.height`])}
                                    type="number"
                                />
                                <TextField
                                    suffix="cm"
                                    styles={{
                                        field: { display: 'none' },
                                    }}
                                />
                            </Columns.Column>
                            <Columns.Column>
                                <TextField
                                    placeholder={tKey('footage')}
                                    value={merchandise.footage?.toString() || ''}
                                    readOnly={isReadOnly || !canEditFootage}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        footage: newVal,
                                    })}
                                    // eslint-disable-next-line max-len
                                    errorMessage={footageError || missingFootageMessage}
                                    required={!isReadOnly}
                                    type="number"
                                    min={0.01}
                                    step={0.01}
                                    suffix={tKey('linearMeterSuffix')}
                                />
                            </Columns.Column>
                            <Columns.Column
                                style={{ marginRight: '10px' }}
                            >
                                <TextField
                                    placeholder={tKey('weight')}
                                    value={merchandise.weight?.toString() || ''}
                                    readOnly={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        weight: +newVal,
                                    })}
                                    // eslint-disable-next-line max-len
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.weight`])}
                                    required={!isReadOnly}
                                    type="number"
                                    suffix="KG"
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns className="is-gapless">
                            <Columns.Column>
                                <TextField
                                    placeholder={isReadOnly ? undefined : tKey('comment')}
                                    value={merchandise.comment ?? ''}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, newVal) => updateMerchandise(merchandiseIndex, {
                                        comment: newVal,
                                    })}
                                    // eslint-disable-next-line max-len
                                    errorMessage={tObj(errorField[`deliveryContentData.merchandises.${merchandiseIndex}.comment`])}
                                />
                            </Columns.Column>
                            <Columns.Column
                                className="is-hidden-mobile"
                                style={{ minWidth: '35px', maxWidth: '35px' }}
                            >
                                {!isReadOnly && (
                                    <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        disabled={isReadOnly}
                                        onClick={() => removeMerchandise(merchandiseIndex)}
                                    />
                                )}
                            </Columns.Column>
                        </Columns>
                        {(!isReadOnly && merchandiseIndex > 0) && (
                            <div className="is-hidden-tablet">
                                <DefaultButton
                                    text="Supprimer"
                                    iconProps={{ iconName: 'Delete' }}
                                    disabled={isReadOnly}
                                    onClick={() => removeMerchandise(merchandiseIndex)}
                                />
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
